import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Session, Tag } from '../../lib/types';
import { useLang, localName, type Lang } from '../../lib/lang';
import { localMinutesOfDay, minutesToLabel } from '../../lib/time';

/**
 * Floating detail card shown while hovering a session block.
 *
 * Rendered through a portal on <body> so it escapes the calendar's
 * `overflow: auto` scroll container, and positioned `fixed` against the
 * anchor's viewport rect (flipping to the other side / clamping when it would
 * run off-screen).
 */

export interface HoverTarget {
  session: Session;
  rect: DOMRect;
  statusLabel: string;
  statusColor: string;
  employeeName?: string | null;
  /** Resolved from the session's tag_ids, already localized by the caller. */
  tags?: Array<{ id: number; name: string; color: string }>;
}

const GAP = 10;
const EDGE = 8;

export function SessionHoverCard({ target }: { target: HoverTarget }): JSX.Element {
  const { session, statusLabel, statusColor, employeeName, tags } = target;
  const { t } = useLang();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const a = target.rect;

    let left = a.right + GAP;
    if (left + width > vw - EDGE) left = a.left - GAP - width;
    left = Math.max(EDGE, Math.min(left, vw - EDGE - width));

    let top = a.top;
    if (top + height > vh - EDGE) top = vh - EDGE - height;
    top = Math.max(EDGE, top);

    setPos({ left, top });
  }, [target]);

  const from = minutesToLabel(localMinutesOfDay(session.start_at));
  const to = minutesToLabel(localMinutesOfDay(session.end_at));
  const who = session.customer_name ?? t({ en: 'Walk-in', ar: 'زائر' });

  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      className={`pointer-events-none fixed z-[100] w-72 max-w-[calc(100vw-16px)] overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-900/10 transition-opacity duration-150 dark:bg-slate-900 dark:ring-white/10 ${pos ? 'opacity-100' : 'opacity-0'}`}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}
    >
      {/* accent bar in the session colour */}
      <div className="h-1 w-full" style={{ background: statusColor }} />

      <div className="space-y-3 p-3">
        {/* customer + status */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{who}</div>
            {session.customer_phone && (
              <div className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400" dir="ltr">
                {session.customer_phone}
              </div>
            )}
          </div>
          <span
            className="flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
            style={{ background: `${statusColor}1f`, color: statusColor }}
          >
            {statusLabel}
          </span>
        </div>

        {/* time + duration */}
        <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-800/60">
          <ClockIcon />
          <div className="min-w-0 text-xs">
            <div className="font-semibold text-slate-700 dark:text-slate-200" dir="ltr">
              {from} <span className="text-slate-400">→</span> {to}
            </div>
            <div className="text-slate-500 dark:text-slate-400">
              {session.duration_minutes} {t({ en: 'min', ar: 'دقيقة' })}
              {employeeName ? ` · ${employeeName}` : ''}
            </div>
          </div>
        </div>

        {/* services */}
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {t({ en: 'Services', ar: 'الخدمات' })}
          </div>
          {session.services.length === 0 ? (
            <div className="text-xs text-slate-400">{t({ en: 'No services', ar: 'لا توجد خدمات' })}</div>
          ) : (
            <ul className="space-y-1">
              {session.services.map((sv) => (
                <li key={sv.id} className="flex items-baseline justify-between gap-2 text-xs">
                  <span className="min-w-0 truncate text-slate-700 dark:text-slate-200">
                    {sv.item_name ?? `#${sv.item_id}`}
                    {sv.qty > 1 && <span className="text-slate-400"> ×{sv.qty}</span>}
                  </span>
                  <span className="flex-none tabular-nums text-slate-400">
                    {sv.duration_minutes} {t({ en: 'min', ar: 'دقيقة' })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {tags && tags.length > 0 && (
          <div>
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {t({ en: 'Tags', ar: 'الوسوم' })}
            </div>
            <div className="flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag.id}
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                  style={{ background: `${tag.color}1f`, color: tag.color }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {session.notes && (
          <div className="border-t border-slate-100 pt-2 text-xs italic text-slate-500 dark:border-slate-800 dark:text-slate-400">
            {session.notes}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function ClockIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4 flex-none text-slate-400">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Turn a session's `tag_ids` into the localized chips the card renders.
 * Returns an empty list until the tag query resolves — the card simply omits
 * the Tags section rather than flashing placeholders.
 */
export function useTagResolver(
  tags: Tag[] | undefined,
  lang: Lang,
): (session: Session) => NonNullable<HoverTarget['tags']> {
  return useMemo(() => {
    const byId = new Map((tags ?? []).map((tg) => [tg.id, tg]));
    return (session: Session) =>
      session.tag_ids
        .map((id) => byId.get(id))
        .filter((tg): tg is Tag => tg != null)
        .map((tg) => ({ id: tg.id, name: localName(lang, tg.name, tg.name_ar), color: tg.color }));
  }, [tags, lang]);
}

/**
 * Hover bookkeeping: open after a short delay (so a cursor sweeping across the
 * grid doesn't flash cards), close immediately, and stay closed while
 * `suppressed` (e.g. a drag is in progress).
 */
export function useHoverCard(suppressed: boolean): {
  target: HoverTarget | null;
  open: (el: HTMLElement, build: () => Omit<HoverTarget, 'rect'>) => void;
  close: () => void;
} {
  const [target, setTarget] = useState<HoverTarget | null>(null);
  const timer = useRef<number | null>(null);

  const clear = (): void => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const close = (): void => {
    clear();
    setTarget(null);
  };

  const open = (el: HTMLElement, build: () => Omit<HoverTarget, 'rect'>): void => {
    clear();
    timer.current = window.setTimeout(() => {
      setTarget({ ...build(), rect: el.getBoundingClientRect() });
    }, 140);
  };

  useEffect(() => {
    if (suppressed) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppressed]);

  // The card is anchored to a rect captured at open time — scrolling would
  // leave it floating next to nothing, so dismiss instead of re-measuring.
  useEffect(() => {
    if (!target) return;
    const onScroll = (): void => close();
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => clear, []);

  return { target: suppressed ? null : target, open, close };
}
